/**
 * The Integracje tab's Kalendarz section (the tab was Kalendarz until 26 October 2026): connect a Google account, see it, reconnect it, disconnect it
 * (calendar-sync-requirements R2, R8, R10), the Apple Calendar button and the standing call link. No handoff — composed from this screen's own classes.
 * Nothing here names an address, token or identifier of the connection (R10).
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { instantWhen } from '~/shared/lib/instantWhen';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { FormBanner } from '~/shared/ui/FormBanner';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AppleCalendarApi } from '../lib/useAppleCalendar';
import type { GoogleCalendarApi } from '../lib/useGoogleCalendar';
import type { StandingCallLinkApi } from '../lib/useStandingCallLink';
import { pickOneCalendarNoteShown } from '../model/appleCalendar';
import { disconnectConfirmBody, type GoogleCalendarView } from '../model/googleCalendar';
import type { GoogleCalendarProblem } from '../model/googleCalendarFlow';
import screen from './AccountScreen.module.css';
import styles from './CalendarTab.module.css';
import { AppleCalendarBlock } from './AppleCalendarBlock';
import { StandingCallLinkBlock } from './StandingCallLinkBlock';

const INK = `${String(ink.ink)} ${String(screen.accountButton)} ${String(screen.accountButtonBold)}`;
const GHOST = `${String(ghost.ghost)} ${String(screen.accountButton)} ${String(screen.canvasGhost)}`;

type AccountView = Extract<GoogleCalendarView, { kind: 'connected' | 'broken' }>;

const PROBLEM_KEY: Readonly<Record<GoogleCalendarProblem, string>> = {
  connectFailed: 'web:account.calendar.connectFailed',
  scopeMissing: 'web:account.calendar.scopeMissing',
  disconnectFailed: 'web:account.calendar.disconnectFailed',
};

interface CalendarTabProps {
  readonly calendar: GoogleCalendarApi;
  readonly apple: AppleCalendarApi;
  readonly standing: StandingCallLinkApi;
}

export function CalendarTab({ calendar, apple, standing }: CalendarTabProps) {
  const { t, i18n } = useTranslation();
  const { view, waiting, skeleton, reveal, state, flow } = calendar;
  /** The button that opened the confirm — where focus returns when it is still there. */
  const askedFrom = useRef<HTMLElement | null>(null);
  const failed = !waiting && state === 'error';
  const busy = flow.busy !== null;
  const connecting = flow.busy === 'connect';
  const problem = flow.problem === null ? null : t(PROBLEM_KEY[flow.problem]);
  const email = view?.kind === 'connected' || view?.kind === 'broken' ? view.email : null;
  /** Google's section is drawn (not its wait, not its error) — the Apple block stands only then too. */
  const googleDrawn = !waiting && !failed && view !== null && view.kind !== 'unavailable';

  /** After a disconnect „Odłącz" is gone; the connect button that replaced it takes focus. */
  const takeFocus = (node: HTMLElement | null) => {
    if (node !== null && flow.focusConnect) {
      node.focus();
      calendar.onFocusTaken();
    }
  };

  const connectButton = (label: string) => (
    <button
      ref={takeFocus}
      type="button"
      className={INK}
      aria-disabled={busy ? 'true' : undefined}
      aria-busy={connecting}
      onClick={busy ? undefined : calendar.onConnect}
    >
      {connecting ? t('web:account.calendar.connecting') : label}
    </button>
  );

  /** Broken, nothing is being written: a calendar never written says nothing rather than „Dodajemy…". */
  const accountLines = (account: AccountView) => (
    <>
      <b className={screen.rowTitle}>
        {account.email === null
          ? t('web:account.calendar.connected')
          : t('web:account.calendar.connectedAs', { email: account.email })}
      </b>
      {account.lastSyncedAt !== null ? (
        <span className={screen.rowNote}>
          {t('web:account.calendar.lastSynced', {
            when: instantWhen(account.lastSyncedAt, calendar.answeredAt, i18n.language),
          })}
        </span>
      ) : (
        account.kind === 'connected' && (
          <span className={screen.rowNote}>{t('web:account.calendar.syncPending')}</span>
        )
      )}
    </>
  );

  const disconnectButton = (
    <button
      type="button"
      className={GHOST}
      aria-disabled={busy ? 'true' : undefined}
      onClick={
        busy
          ? undefined
          : (event) => {
              askedFrom.current = event.currentTarget;
              calendar.onAskDisconnect();
            }
      }
    >
      {t('web:account.calendar.disconnect')}
    </button>
  );

  return (
    <div className={screen.inner}>
      <div className={screen.lede}>
        <h2 className={screen.h2}>{t('web:account.calendar.section')}</h2>
        {/* Google OR Apple (owner, 29 October 2026): both would draw every session twice. */}
        {googleDrawn && pickOneCalendarNoteShown(view, apple.view) ? (
          <p className={`${String(screen.ledeText)} ${reveal}`}>{t('web:account.calendar.pickOne')}</p>
        ) : null}
        <p className={screen.ledeText}>{t('web:account.calendar.lede')}</p>
      </div>

      {waiting || (view === null && !failed) ? (
        <div className={screen.block} aria-busy={waiting}>
          {waiting && <span className={hidden.srOnly}>{t('web:account.calendar.waiting')}</span>}
          {skeleton && <i className={`${String(skel.bar)} ${String(styles.noteBar)}`} />}
          {skeleton && <i className={`${String(skel.bar)} ${String(styles.buttonBar)}`} />}
        </div>
      ) : failed ? (
        <div className={`${String(screen.block)} ${reveal}`}>
          <h3 className={screen.blockTitle}>{t('web:account.calendar.loadErrorTitle')}</h3>
          <FormBanner message={t('web:account.calendar.loadErrorBody')} />
          <button
            type="button"
            className={`${GHOST} ${String(styles.retry)}`}
            onClick={calendar.onRetry}
          >
            {t('common.retry')}
          </button>
        </div>
      ) : view?.kind === 'connected' ? (
        // The connected account's e-mail: kept out of session replay here, the broken block and the confirm.
        <div className={`${String(screen.block)} ${reveal}`} data-clarity-mask="True">
          {accountLines(view)}
          <div className={styles.actions}>{disconnectButton}</div>
          <FormBanner message={problem} />
        </div>
      ) : view?.kind === 'broken' ? (
        <div className={`${String(screen.block)} ${reveal}`} data-clarity-mask="True">
          {/* R10: which account, and how stale its calendar now is. */}
          {accountLines(view)}
          {/* A state, not the answer to a press: drawn, not shouted as an alert. */}
          <b className={`${String(screen.rowTitle)} ${String(styles.brokenNote)}`}>
            {t('web:account.calendar.broken')}
          </b>
          {/* R2: reconnecting opens the consent page too, so what the grant is for is said first. */}
          <span className={screen.rowNote}>{t('web:account.calendar.disclosure')}</span>
          <div className={styles.actions}>
            {connectButton(t('web:account.calendar.reconnect'))}
            {disconnectButton}
          </div>
          <FormBanner message={problem} />
        </div>
      ) : (
        // R2: what the grant is for is said here, before the consent page opens.
        <div className={`${String(screen.block)} ${reveal}`}>
          <span className={screen.rowNote}>{t('web:account.calendar.disclosure')}</span>
          <div className={styles.actions}>{connectButton(t('web:account.calendar.connect'))}</div>
          <FormBanner message={problem} />
        </div>
      )}

      {/* With the account's own block, so they neither stand under the skeleton nor move when it fills. */}
      {googleDrawn && (
        <>
          <AppleCalendarBlock apple={apple} className={reveal} />
          <StandingCallLinkBlock standing={standing} className={reveal} />
        </>
      )}

      <ConfirmDialog
        prompt={
          flow.confirm
            ? {
                title: t('web:account.calendar.disconnectConfirmTitle'),
                description: t(`web:account.calendar.${disconnectConfirmBody(view)}`, {
                  email: email ?? '',
                }),
                confirmLabel: t('web:account.calendar.disconnectConfirm'),
                cancelLabel: t('common.cancel'),
                destructiveConfirm: true,
                masked: email !== null,
                busy: flow.busy === 'disconnect',
              }
            : null
        }
        onCancel={calendar.onCancelConfirm}
        onConfirm={calendar.onConfirmDisconnect}
        finalFocus={askedFrom}
      />
    </div>
  );
}
